import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, MessageCircle, Trash2, Clock } from "lucide-react";
import { api, todayISO, typeBadge, fmt, idDate, shortDate } from "../lib/dimsum";

const inputCls =
  "w-full rounded-2xl border-2 border-stone-300 bg-stone-50 px-4 py-3.5 text-base sm:text-lg font-semibold text-stone-900 focus:border-rose-500 focus:bg-white focus:outline-none transition-colors";
const labelCls = "text-sm sm:text-base font-bold text-stone-800 mb-1.5 block";

const Stepper = ({ value, onChange, testid }) => (
  <input
    data-testid={testid}
    type="number"
    inputMode="decimal"
    placeholder="0"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={inputCls + " text-center font-mono-num"}
  />
);

const PersonAutocomplete = ({ label, value, onChange, options, exclude, testid, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ex = (exclude || "").trim().toLowerCase();
  const q = (value || "").trim().toLowerCase();
  const matches = options.filter(
    (o) => o.toLowerCase().includes(q) && o !== value && o.toLowerCase() !== ex
  );
  const canShow = q.length >= 1;
  return (
    <div className="relative">
      <label className={labelCls}>{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && canShow && matches.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1.5 w-full max-h-52 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg"
        >
          {matches.map((o) => (
            <li key={o}>
              <button
                type="button"
                data-testid={`${testid}-option-${o}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 font-semibold text-stone-800 hover:bg-emerald-50 transition-colors"
              >
                {o}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TypeAutocomplete = ({ options, selected, onPick, testid, placeholder }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = options.filter(
    (o) => !selected.includes(o.code) && o.code.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="relative mb-4">
      <input
        data-testid={testid}
        type="text"
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && matches.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1.5 w-full max-h-56 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg"
        >
          {matches.map((o) => (
            <li key={o.code}>
              <button
                type="button"
                data-testid={`${testid}-option-${o.code}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(o.code);
                  setQ("");
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 font-bold text-stone-800 hover:bg-amber-50 transition-colors"
              >
                {o.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const InputLembur = ({ teams = [], doughTypes = [], isAdmin = false, onSaved }) => {
  const [date, setDate] = useState(todayISO());
  const [person1, setPerson1] = useState("");
  const [person2, setPerson2] = useState("");
  const [selected, setSelected] = useState([]);
  const [kgMap, setKgMap] = useState({});
  const [paxMap, setPaxMap] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const [summaryPeriod, setSummaryPeriod] = useState("day");
  const [entries, setEntries] = useState([]);

  const memberOptions = useMemo(
    () => Array.from(new Set(teams.flatMap((t) => t.members))).sort(),
    [teams]
  );

  const sameName =
    person1.trim() !== "" && person1.trim().toLowerCase() === person2.trim().toLowerCase();

  const isJumbo = (code) => !!doughTypes.find((d) => d.code === code)?.is_jumbo;

  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  const loadEntries = () => {
    const now = new Date();
    let params = "";
    if (summaryPeriod === "day") {
      params = `?start=${iso(now)}&end=${iso(now)}`;
    } else if (summaryPeriod === "week") {
      const dow = (now.getDay() + 6) % 7;
      const mon = new Date(now);
      mon.setDate(now.getDate() - dow);
      const e = new Date(mon);
      e.setDate(mon.getDate() + 4);
      const s = new Date(e);
      s.setDate(e.getDate() - 7);
      params = `?start=${iso(s)}&end=${iso(e)}`;
    } else if (summaryPeriod === "month") {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      params = `?start=${iso(s)}&end=${iso(e)}`;
    }
    api
      .get(`/lembur${params}`)
      .then((r) => setEntries(r.data || []))
      .catch(() => setEntries([]));
  };

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryPeriod, saving]);

  const summary = useMemo(() => {
    let total = 0;
    let pax = 0;
    let j = 0;
    let m = 0;
    entries.forEach((en) => {
      (en.items || []).forEach((it) => {
        const kg = Number(it.kg) || 0;
        total += kg;
        pax += Number(it.pax) || 0;
        if (it.kategori === "J") j += kg;
        else m += kg;
      });
    });
    return { total, pax, j, m, count: entries.length };
  }, [entries]);

  const toggleType = (code) => {
    setSelected((prev) => {
      if (prev.includes(code)) {
        setKgMap((p) => {
          const next = { ...p };
          delete next[code];
          return next;
        });
        setPaxMap((p) => {
          const next = { ...p };
          delete next[code];
          return next;
        });
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const reset = () => {
    setPerson1("");
    setPerson2("");
    setSelected([]);
    setKgMap({});
    setPaxMap({});
    setNote("");
  };

  const buildItems = () =>
    selected
      .filter((c) => Number(kgMap[c]) > 0 || Number(paxMap[c]) > 0)
      .map((c) => ({
        produk: c,
        kategori: isJumbo(c) ? "J" : "M",
        kg: Number(kgMap[c]) || 0,
        pax: Number(paxMap[c]) || 0,
      }));

  const buildWaText = (members, items) => {
    const tKg = items.reduce((s, i) => s + Number(i.kg || 0), 0);
    const tPax = items.reduce((s, i) => s + Number(i.pax || 0), 0);
    const w = Math.max(6, ...items.map((i) => i.produk.length), "TOTAL".length);
    const line = (label, val, unit) => label.padEnd(w) + " : " + fmt(val) + " " + unit;
    let table = "ADONAN (kg):\n";
    items.forEach((i) => (table += line(i.produk, i.kg, "kg") + "\n"));
    table += line("TOTAL", tKg, "kg") + "\n\n";
    table += "DIMSUM (pax):\n";
    items.forEach((i) => (table += line(i.produk, i.pax, "pax") + "\n"));
    table += line("TOTAL", tPax, "pax");
    const head =
      `*REKAP LEMBUR DIMSUM*\n\n` +
      `Tanggal: ${idDate(date)}\n` +
      `Peserta: ${members.join(" & ")}\n\n`;
    const footer = note.trim() ? `\nCatatan: ${note.trim()}\n` : "";
    return head + "```\n" + table + "\n```\n" + footer;
  };

  const validate = () => {
    const members = [person1, person2].map((s) => s.trim()).filter(Boolean);
    if (members.length === 0) {
      toast.error("Isi minimal nama Peserta 1");
      return null;
    }
    if (sameName) {
      toast.error("Nama Peserta 1 & 2 tidak boleh sama");
      return null;
    }
    const invalid = members.filter(
      (mm) => !memberOptions.some((o) => o.trim().toLowerCase() === mm.trim().toLowerCase())
    );
    if (invalid.length) {
      toast.error(`Nama peserta tidak terdaftar: ${invalid.join(", ")}.`);
      return null;
    }
    const items = buildItems();
    if (items.length === 0) {
      toast.error("Pilih & isi minimal 1 produk (Kg/Pax)");
      return null;
    }
    return { date, members, items, note: note.trim() };
  };

  const save = async () => {
    const payload = validate();
    if (!payload) return;
    setSaving(true);
    try {
      await api.post("/lembur", payload);
      toast.success("✅ Lembur tersimpan & masuk ke perhitungan Gaji!");
      window.open(
        `https://wa.me/?text=${encodeURIComponent(buildWaText(payload.members, payload.items))}`,
        "_blank"
      );
      reset();
      onSaved?.();
      setSaving((s) => !s); // toggle to refresh summary list
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan lembur");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus data lembur ini?")) return;
    try {
      await api.delete(`/lembur/${id}`);
      toast.success("🗑️ Data lembur dihapus");
      loadEntries();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus (butuh login admin/master)");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="lembur-entry-form">
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center w-10 h-10 rounded-2xl bg-sky-600 text-white shrink-0">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-extrabold text-stone-900 leading-none">Input Lembur</h2>
            <p className="text-xs font-semibold text-stone-500 mt-0.5">Catat kg lembur per peserta</p>
          </div>
        </div>

        <div>
          <label className={labelCls}>Tanggal</label>
          <input
            data-testid="lembur-input-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <PersonAutocomplete
            label="Peserta 1"
            value={person1}
            onChange={setPerson1}
            options={memberOptions}
            exclude={person2}
            testid="lembur-person-1"
            placeholder="Ketik / pilih nama…"
          />
          <div>
            <PersonAutocomplete
              label="Peserta 2 (opsional)"
              value={person2}
              onChange={setPerson2}
              options={memberOptions}
              exclude={person1}
              testid="lembur-person-2"
              placeholder="Ketik / pilih nama…"
            />
            {sameName && (
              <p data-testid="lembur-duplicate-warning" className="mt-1.5 text-sm font-bold text-rose-600">
                Nama Peserta 1 & 2 tidak boleh sama
              </p>
            )}
          </div>
        </div>

        <div>
          <label className={labelCls}>Jenis, Adonan (Kg) & Dimsum (Pax)</label>
          <TypeAutocomplete
            options={doughTypes}
            selected={selected}
            onPick={toggleType}
            testid="lembur-type-search"
            placeholder="Ketik jenis… (mis. ARJ)"
          />
          {selected.length === 0 ? (
            <p className="rounded-2xl bg-stone-50 border border-dashed border-stone-300 px-4 py-5 text-center text-sm font-semibold text-stone-400">
              Belum ada produk dipilih.
            </p>
          ) : (
            <div className="space-y-4">
              {[...selected]
                .reverse()
                .map((code) => doughTypes.find((x) => x.code === code))
                .filter(Boolean)
                .map((d) => (
                  <div key={d.code} className="rounded-2xl bg-sky-50/60 border border-sky-200 p-3.5">
                    <div className="mb-2 relative flex items-center justify-center">
                      <span className={`inline-block rounded-lg border px-2.5 py-1 text-xs font-bold ${typeBadge(d.code)}`}>
                        {d.name}
                      </span>
                      <button
                        type="button"
                        data-testid={`lembur-remove-${d.code}`}
                        onClick={() => toggleType(d.code)}
                        className="absolute right-0 grid place-items-center w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 active:scale-95 transition-transform"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="mb-1.5 block text-center text-xs font-extrabold uppercase tracking-wider text-rose-600">
                          Kg
                        </span>
                        <Stepper
                          value={kgMap[d.code] ?? ""}
                          onChange={(v) => setKgMap((p) => ({ ...p, [d.code]: v }))}
                          testid={`lembur-input-kg-${d.code}`}
                        />
                      </div>
                      <div>
                        <span className="mb-1.5 block text-center text-xs font-extrabold uppercase tracking-wider text-emerald-600">
                          Pax
                        </span>
                        <Stepper
                          value={paxMap[d.code] ?? ""}
                          onChange={(v) => setPaxMap((p) => ({ ...p, [d.code]: v }))}
                          testid={`lembur-input-pax-${d.code}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Catatan</label>
          <textarea
            data-testid="lembur-input-notes"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Catatan opsional…"
            className={inputCls}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            data-testid="lembur-submit-button"
            onClick={save}
            disabled={saving}
            className="flex-1 min-h-[52px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-md active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <MessageCircle className="w-5 h-5" /> {saving ? "Menyimpan…" : "Simpan & Kirim WA"}
          </button>
          <button
            data-testid="lembur-reset-button"
            onClick={reset}
            className="min-h-[52px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold px-6 flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" /> Reset
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border-2 border-sky-200 rounded-3xl p-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-sky-700 mb-2">Ringkasan Lembur</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              ["day", "Hari"],
              ["week", "Pekan"],
              ["month", "Bulan"],
              ["all", "Semua"],
            ].map(([p, label]) => (
              <button
                key={p}
                data-testid={`lembur-filter-${p}`}
                onClick={() => setSummaryPeriod(p)}
                className={`min-h-[36px] rounded-xl px-3 font-bold text-xs transition-colors ${
                  summaryPeriod === p ? "bg-sky-600 text-white shadow" : "bg-white/70 text-stone-600 hover:bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-semibold text-stone-500">Total Kg</p>
              <p data-testid="lembur-summary-total" className="font-mono-num text-3xl font-extrabold text-sky-700">
                {fmt(summary.total)} <span className="text-base">kg</span>
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-500">Total Pax</p>
              <p data-testid="lembur-summary-pax" className="font-mono-num text-3xl font-extrabold text-emerald-700">
                {fmt(summary.pax)} <span className="text-base">pax</span>
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/70 px-3 py-2">
              <p className="text-xs font-semibold text-stone-500">Lembur J (kg)</p>
              <p data-testid="lembur-summary-j" className="font-mono-num text-xl font-extrabold text-indigo-700">{fmt(summary.j)} kg</p>
            </div>
            <div className="rounded-2xl bg-white/70 px-3 py-2">
              <p className="text-xs font-semibold text-stone-500">Lembur M (kg)</p>
              <p data-testid="lembur-summary-m" className="font-mono-num text-xl font-extrabold text-cyan-700">{fmt(summary.m)} kg</p>
            </div>
          </div>
          <p className="mt-2 text-xs font-semibold text-stone-400">{summary.count} data lembur</p>
        </div>

        <div className="bg-white border-2 border-stone-200 rounded-3xl p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Data Lembur</p>
          {entries.length === 0 ? (
            <p className="text-sm font-semibold text-stone-400">Belum ada data lembur.</p>
          ) : (
            <div className="space-y-2" data-testid="lembur-entry-list">
              {entries.map((en) => (
                <div key={en.id} data-testid={`lembur-entry-${en.id}`} className="rounded-2xl bg-stone-50 border border-stone-200 px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-stone-800 truncate">{(en.members || []).join(" & ") || "-"}</p>
                      <p className="text-xs text-stone-400">{shortDate(en.date)}</p>
                    </div>
                    {isAdmin && (
                      <button
                        data-testid={`lembur-delete-${en.id}`}
                        onClick={() => del(en.id)}
                        title="Hapus data lembur"
                        className="grid place-items-center w-7 h-7 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(en.items || []).map((it, idx) => (
                      <span key={idx} className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${typeBadge(it.produk)}`}>
                        {it.produk} · {fmt(it.kg)}kg · {fmt(it.pax)}pax
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
